'use client';

import { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { useGlobalCompany } from './useGlobalCompany';
import { useCurrentUser } from './useCurrentUser';
import { FeatureKeyStatusDTO } from '@/types';

interface PermissionsContextType {
    /** Check if a feature key is allowed for the current user in the current organization. */
    isFeatureAllowed: (featureKey: string) => boolean;
    /** Whether the permissions data is still loading */
    isLoading: boolean;
    /** Raw permissions map: feature key → allowed */
    permissions: Map<string, boolean>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
    children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
    const { selectedCompanyId } = useGlobalCompany();
    const { isLoading: roleLoading } = useCurrentUser();

    // Admins used to skip this fetch entirely, on the assumption that they can do everything.
    // That stopped being true with subscription plans: an admin still bypasses role and
    // override checks, but not the organization's plan. The backend now returns a real feature
    // map for admins too (plan-denied features come back as not allowed).
    const { data: featureStatuses } = useQuery<FeatureKeyStatusDTO[] | null>({
        queryKey: ['user-features', selectedCompanyId],
        queryFn: () => apiService.getMyFeatures(''),
        // When no company is selected the backend resolves the union across all user's companies.
        enabled: !roleLoading,
        retry: 3,
        // Switching company changes the query key, so `data` would go undefined and every
        // feature would read as denied for the length of one fetch — long enough for gated
        // sections to vanish and for SettingsPage to bounce the user off the tab they were on.
        // Keeping the previous map costs a moment of the old company's gating in the UI only;
        // the server is still the one enforcing.
        placeholderData: keepPreviousData,
    });

    // Readiness is "we actually have a map", NOT "a fetch finished". isFetched is also true
    // after a *failed* fetch, which would make every feature look denied and strip the whole
    // UI on one transient 500. TanStack keeps the last successful data across a failed
    // refetch, so this keeps working from the last known-good map instead.
    const permissionsReady = featureStatuses !== undefined;

    // null means the backend has no feature map for this user — a pre-subscription build
    // answering 204 for an admin. Fall back to what that build's UI did (allow everything) so a
    // backend rollback degrades to the old behaviour instead of stripping the admin UI. The
    // server is still the enforcement boundary either way.
    const legacyAllowAll = featureStatuses === null;

    const permissions = useMemo(() => {
        const map = new Map<string, boolean>();
        for (const feature of featureStatuses ?? []) {
            map.set(feature.feature_key, feature.allowed);
        }
        return map;
    }, [featureStatuses]);

    const isFeatureAllowed = useCallback((featureKey: string): boolean => {
        // While loading, default to false — menu items appear only after permissions are confirmed
        if (roleLoading || !permissionsReady) return false;
        if (legacyAllowAll) return true;
        // Look up the permission; default to false if not found
        return permissions.get(featureKey) ?? false;
    }, [roleLoading, permissionsReady, legacyAllowAll, permissions]);

    const isLoading = roleLoading || !permissionsReady;

    const value = useMemo(() => ({
        isFeatureAllowed,
        isLoading,
        permissions,
    }), [isFeatureAllowed, isLoading, permissions]);

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
}

export function usePermissions() {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error('usePermissions must be used within a PermissionsProvider');
    }
    return context;
}
